import * as z from 'zod';

export const CreateBillableItemRequest = z.object({
  billableItem: z
    .object({
      _id: z
        .string()
        .describe('Billable item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the billable item was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the billable item was last updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Billable item name, for display on invoices.')
        .min(1)
        .max(200),
      description: z
        .string()
        .describe(
          'Detailed description of the billable item. Provides additional information about the product or service.'
        )
        .min(1)
        .max(600)
        .optional()
        .nullable(),
      price: z
        .string()
        .describe(
          'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
        ),
      taxGroupId: z
        .string()
        .describe(
          'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      image: z
        .string()
        .describe(
          'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Billable item to create.'),
});
export const CreateBillableItemResponse = z.object({
  _id: z
    .string()
    .describe('Billable item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the billable item was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the billable item was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Billable item name, for display on invoices.')
    .min(1)
    .max(200)
    .optional(),
  description: z
    .string()
    .describe(
      'Detailed description of the billable item. Provides additional information about the product or service.'
    )
    .min(1)
    .max(600)
    .optional()
    .nullable(),
  price: z
    .string()
    .describe(
      'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
    )
    .optional(),
  taxGroupId: z
    .string()
    .describe(
      'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  image: z
    .string()
    .describe(
      'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags.')
    .optional(),
});
export const GetBillableItemRequest = z.object({
  billableItemId: z
    .string()
    .describe('Billable item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetBillableItemResponse = z.object({
  _id: z
    .string()
    .describe('Billable item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the billable item was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the billable item was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Billable item name, for display on invoices.')
    .min(1)
    .max(200)
    .optional(),
  description: z
    .string()
    .describe(
      'Detailed description of the billable item. Provides additional information about the product or service.'
    )
    .min(1)
    .max(600)
    .optional()
    .nullable(),
  price: z
    .string()
    .describe(
      'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
    )
    .optional(),
  taxGroupId: z
    .string()
    .describe(
      'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  image: z
    .string()
    .describe(
      'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags.')
    .optional(),
});
export const UpdateBillableItemRequest = z.object({
  _id: z
    .string()
    .describe('Billable item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  billableItem: z
    .object({
      _id: z
        .string()
        .describe('Billable item ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the billable item was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the billable item was last updated.')
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Billable item name, for display on invoices.')
        .min(1)
        .max(200),
      description: z
        .string()
        .describe(
          'Detailed description of the billable item. Provides additional information about the product or service.'
        )
        .min(1)
        .max(600)
        .optional()
        .nullable(),
      price: z
        .string()
        .describe(
          'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
        ),
      taxGroupId: z
        .string()
        .describe(
          'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      image: z
        .string()
        .describe(
          'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
        )
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
        )
        .optional(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags.')
        .optional(),
    })
    .describe('Billable item to update. May be partial.'),
});
export const UpdateBillableItemResponse = z.object({
  _id: z
    .string()
    .describe('Billable item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the billable item was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the billable item was last updated.')
    .optional()
    .nullable(),
  name: z
    .string()
    .describe('Billable item name, for display on invoices.')
    .min(1)
    .max(200)
    .optional(),
  description: z
    .string()
    .describe(
      'Detailed description of the billable item. Provides additional information about the product or service.'
    )
    .min(1)
    .max(600)
    .optional()
    .nullable(),
  price: z
    .string()
    .describe(
      'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
    )
    .optional(),
  taxGroupId: z
    .string()
    .describe(
      'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  image: z
    .string()
    .describe(
      'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
    )
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe(
      'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
    )
    .optional(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe('Tags.')
    .optional(),
});
export const DeleteBillableItemRequest = z.object({
  billableItemId: z
    .string()
    .describe('Billable item ID to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteBillableItemResponse = z.object({});
export const QueryBillableItemsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryBillableItemsResponse = z.object({
  billableItems: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Billable item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the billable item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the billable item was last updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Billable item name, for display on invoices.')
          .min(1)
          .max(200)
          .optional(),
        description: z
          .string()
          .describe(
            'Detailed description of the billable item. Provides additional information about the product or service.'
          )
          .min(1)
          .max(600)
          .optional()
          .nullable(),
        price: z
          .string()
          .describe(
            'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
          )
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        image: z
          .string()
          .describe(
            'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const BulkCreateBillableItemsRequest = z.object({
  billableItems: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Billable item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the billable item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the billable item was last updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Billable item name, for display on invoices.')
          .min(1)
          .max(200)
          .optional(),
        description: z
          .string()
          .describe(
            'Detailed description of the billable item. Provides additional information about the product or service.'
          )
          .min(1)
          .max(600)
          .optional()
          .nullable(),
        price: z
          .string()
          .describe(
            'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
          )
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        image: z
          .string()
          .describe(
            'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags.')
          .optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the created billable items in the response.'
        )
        .optional(),
    })
    .describe('Field options.')
    .optional(),
});
export const BulkCreateBillableItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe("Billable item's create operation metadata.")
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Billable item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the billable item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the billable item was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Billable item name, for display on invoices.')
              .min(1)
              .max(200)
              .optional(),
            description: z
              .string()
              .describe(
                'Detailed description of the billable item. Provides additional information about the product or service.'
              )
              .min(1)
              .max(600)
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
              )
              .optional(),
            taxGroupId: z
              .string()
              .describe(
                'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            image: z
              .string()
              .describe(
                'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe('Tags.')
              .optional(),
          })
          .describe(
            'Created billable item if `returnEntity` was set `true` in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk create operation.')
    .optional(),
});
export const BulkUpdateBillableItemsRequest = z.object({
  billableItems: z
    .array(
      z.object({
        billableItem: z
          .object({
            _id: z
              .string()
              .describe('Billable item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
              ),
            _createdDate: z
              .date()
              .describe('Date and time the billable item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the billable item was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Billable item name, for display on invoices.')
              .min(1)
              .max(200)
              .optional(),
            description: z
              .string()
              .describe(
                'Detailed description of the billable item. Provides additional information about the product or service.'
              )
              .min(1)
              .max(600)
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
              )
              .optional(),
            taxGroupId: z
              .string()
              .describe(
                'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            image: z
              .string()
              .describe(
                'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe('Tags.')
              .optional(),
          })
          .describe('Billable items to update. May be partial.'),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Return updated billable items in the response.')
        .optional(),
    })
    .describe('Field options.')
    .optional(),
});
export const BulkUpdateBillableItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe("Billable item's update operation metadata.")
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Billable item ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe('Date and time the billable item was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the billable item was last updated.')
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Billable item name, for display on invoices.')
              .min(1)
              .max(200)
              .optional(),
            description: z
              .string()
              .describe(
                'Detailed description of the billable item. Provides additional information about the product or service.'
              )
              .min(1)
              .max(600)
              .optional()
              .nullable(),
            price: z
              .string()
              .describe(
                'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
              )
              .optional(),
            taxGroupId: z
              .string()
              .describe(
                'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            image: z
              .string()
              .describe(
                'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
              )
              .optional(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe(
                'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
              )
              .optional(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe('Tags.')
              .optional(),
          })
          .describe(
            'Updated billable item if `returnEntity` was set `true` in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk update operation.')
    .optional(),
});
export const BulkDeleteBillableItemsRequest = z.object({
  billableItemIds: z.array(z.string()).min(1).max(100),
});
export const BulkDeleteBillableItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe("Billable item's delete operation metadata.")
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata regarding the bulk delete operation.')
    .optional(),
});
export const BulkUpdateBillableItemTagsRequest = z.object({
  billableItemIds: z.array(z.string()).min(1).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to assign.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to unassign.')
        .optional(),
    })
    .describe('Field options.')
    .optional(),
});
export const BulkUpdateBillableItemTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe("Billable item's update tags operation metadata.")
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk update tags metadata.')
    .optional(),
});
export const BulkUpdateBillableItemTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe('Filter to select relevant billable items.'),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to assign.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('List of tags to unassign.')
        .optional(),
    })
    .describe('Field options.')
    .optional(),
});
export const BulkUpdateBillableItemTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const SearchBillableItemsRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'A filter object. See documentation [here](https://bo.wix.com/wix-docs/rnd/platformization-guidelines/api-query-language#platformization-guidelines_api-query-language_defining-in-protobuf)'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'User-defined name of aggregation, should be unique, will appear in aggregation results'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggregate by, use dot notation to specify json path'
                  )
                  .max(200)
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe(
                            'Should sort by number of matches or value of the field'
                          )
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe(
                            'Should sort in ascending or descending order'
                          )
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'How many aggregations would you like to return? Can be between 1 and 250. 10 is the default.'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Should missing values be included or excluded from the aggregation results. Default is EXCLUDE'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Can specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe('Options for including missing values'),
                        }),
                      ])
                    )
                    .describe('Value aggregation'),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if to is not given'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if from is not given'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe('Range aggregation'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe(
                          'Define the operator for the scalar aggregation'
                        )
                        .optional(),
                    })
                    .describe('Scalar aggregation'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation')
                        .optional(),
                    })
                    .describe('Date histogram aggregation'),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'User-defined name of aggregation, should be unique, will appear in aggregation results'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Field to aggregate by, use dont notation to specify json path'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe(
                                          'Should sort by number of matches or value of the field'
                                        )
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe(
                                          'Should sort in ascending or descending order'
                                        )
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'How many aggregations would you like to return? Can be between 1 and 250. 10 is the default.'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Should missing values be included or excluded from the aggregation results. Default is EXCLUDE'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Can specify custom bucket name. Defaults are [string -> "N/A"], [int -> "0"], [bool -> "false"] ...'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe('Value aggregation'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if to is not given'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if from is not given'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe('Range aggregation'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Define the operator for the scalar aggregation'
                                      )
                                      .optional(),
                                  })
                                  .describe('Scalar aggregation'),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation'
                                      )
                                      .optional(),
                                  })
                                  .describe('Date histogram aggregation'),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe('Nested aggregation'),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Flag if should use auto fuzzy search (allowing typos by a managed proximity algorithm)'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'UTC offset or IANA time zone. Valid values are\nISO 8601 UTC offsets, such as +02:00 or -06:00,\nand IANA time zone IDs, such as Europe/Rome\n\nAffects all filters and aggregations returned values.\nYou may override this behavior in a specific filter by providing\ntimestamps including time zone. e.g. `"2023-12-20T10:52:34.795Z"`'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              "Cursor pointing to page of results.\nWhen requesting 'cursor_paging.cursor', no `filter`, `sort` or `search` can be provided."
            ),
        }),
      ])
    )
    .describe('Search options.'),
});
export const SearchBillableItemsResponse = z.object({
  billableItems: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Billable item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the billable item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the billable item.\n\nIgnored when creating a billable item.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the billable item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the billable item was last updated.')
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Billable item name, for display on invoices.')
          .min(1)
          .max(200)
          .optional(),
        description: z
          .string()
          .describe(
            'Detailed description of the billable item. Provides additional information about the product or service.'
          )
          .min(1)
          .max(600)
          .optional()
          .nullable(),
        price: z
          .string()
          .describe(
            'Price of the billable item in decimal format with up to 4 decimal places.\nRepresents the base cost before taxes are applied. This is a required field when creating a new billable item.'
          )
          .optional(),
        taxGroupId: z
          .string()
          .describe(
            'Reference to a tax group that defines the tax rules applicable to this billable item.\nUse the Tax Groups API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/billing/tax-groups/introduction) | [REST](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-groups/introduction)) to retrieve available tax groups for the business.\nThe tax group determines how taxes are calculated when this item is added to an invoice.\nIf not specified, the default tax rules for the business will apply.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        image: z
          .string()
          .describe(
            'Billable item image, stored in the Wix Media Manager.\nUpload an image with the Media Manager API ([SDK](https://dev.wix.com/docs/sdk/backend-modules/media/introduction) | [REST](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction)) or the site dashboard before associating it to a billable item.'
          )
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the billable item.\nExtended fields must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe('Tags.')
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'User-defined name of aggregation as derived from search request'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Type of aggregation that must match provided kind as derived from search request'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by as derived from search request'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities with this value')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of entities in this range')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation')
                      .optional(),
                  })
                  .describe('Scalar aggregation results'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'User-defined name of aggregation, matches the one provided in request'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe(
                                    'Type of aggregation that matches result'
                                  )
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field to aggregate by, matches the one provided in request'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities with this value'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Count of entities in this range'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Date in ISO 8601 format')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities in this range'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Count of entities with this value'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Response aggregation data.')
    .optional(),
});
